import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell, ImagePlaceholder } from "@/components/site/SiteShell";
import { PageHero, Section, Breadcrumbs, Prose } from "@/components/site/Sections";
import { BLOG_POSTS } from "@/components/site/blog-posts";

const TITLE = "Elektriker-bloggen, råd om el, energi & varme";
const DESCRIPTION =
  "Elektriker-bloggen: praktiske råd og dybdegående artikler om elinstallationer, energi, varmepumper, sikkerhed og besparelser. Skrevet af autoriserede elektrikere.";

export const Route = createFileRoute("/blog/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: "/blog" },
    ],
    links: [{ rel: "canonical", href: "https://elektriker-elektriker.dk/blog" }],
  }),
  component: BlogIndex,
});

function BlogIndex() {
  return (
    <SiteShell>
      <Breadcrumbs items={[{ label: "Forside", to: "/" }, { label: "Blog" }]} />
      <PageHero
        variant="centered"
        eyebrow="Blog · Skrevet af elektrikere"
        title="Råd & viden om el, energi og varme"
        intro="Vi deler vores erfaring fra felten, fra varmepumper og solceller til klassiske el-spørgsmål om HPFI, jordfejl og standby-forbrug. Hver artikel er skrevet af en autoriseret elektriker, ikke af et marketingbureau."
        imageLabel="Blog hero"
      />

      <Section>
        <Prose>
          <p>
            Vi får hver dag de samme spørgsmål fra kunder, så her samler vi de gode forklaringer
            i artikelform. Læs om hvordan HPFI virker, hvorfor LED ikke altid er billigere, om dit
            fjernsyn faktisk bruger meget standby-strøm, og om hvad det egentlig kræver at skifte
            oliefyret ud med en varmepumpe. Bruger du tid på at læse en af artiklerne, sparer du
            sandsynligvis tid (og penge) når du senere skal beslutte noget i dit eget hjem.
          </p>
        </Prose>
      </Section>

      <Section className="!pt-0">
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {BLOG_POSTS.map((p) => (
            <article key={p.slug} className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card transition hover:shadow-[var(--shadow-soft)]">
              <ImagePlaceholder label={p.category} ratio="aspect-[16/10]" className="rounded-none border-0 border-b" />
              <div className="flex flex-1 flex-col p-6">
                <span className="text-xs font-semibold uppercase tracking-wider text-primary">{p.category}</span>
                <h2 className="mt-2 text-lg font-semibold text-[var(--ink)]">
                  <Link to={`/${p.slug}` as string} className="hover:underline">{p.title}</Link>
                </h2>
                <p className="mt-2 text-sm text-muted-foreground">{p.excerpt}</p>
                <div className="mt-4 flex items-center justify-between pt-4">
                  <time className="text-xs text-muted-foreground" dateTime={p.date}>
                    {new Date(p.date).toLocaleDateString("da-DK", { year: "numeric", month: "long", day: "numeric" })}
                  </time>
                  <Link to={`/${p.slug}` as string} className="text-sm font-semibold text-[var(--ink)] group-hover:text-primary">Læs →</Link>
                </div>
              </div>
            </article>
          ))}
        </div>
      </Section>
    </SiteShell>
  );
}
